﻿import { useState, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Check, ChevronsUpDown, Loader2, Search, Users } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { useRequestGetUsers } from '@/hooks/httpRequest';
import { useDebounce } from '@/hooks';
import { UserAccountStatusEnum } from '@/enums';
import { cn } from '@/lib/utils';
import type { IAdminMessageFormState, ISelectedUser } from '@/interfaces';
import type { ISearchUserData } from '@/lib/interface';

type UserMultiSelectField = 'selectedUsers' | 'excludedUsers';

interface UserMultiSelectProps {
  name?: UserMultiSelectField;
  label?: string;
  showLabel?: boolean;
}

const UserMultiSelect = ({ name = 'selectedUsers', label, showLabel = true }: UserMultiSelectProps) => {
  const { control, setValue } = useFormContext<IAdminMessageFormState>();
  const selectedUsers = useWatch({ control, name }) ?? [];

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { users, isLoading } = useRequestGetUsers(
    {
      accountStatus: [UserAccountStatusEnum.ACTIVE],
      limit: 10,
      emailOrUsername: debouncedSearchTerm || undefined
    },
    open
  );

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);

  const selectedSet = useMemo(() => new Set(selectedUsers.map((u: ISelectedUser) => u.id)), [selectedUsers]);
  const isSelected = (id: string) => selectedSet.has(id);

  const toggleUser = (user: { _id: string; username: string; email: string }) => {
    const next: ISelectedUser[] = selectedSet.has(user._id)
      ? selectedUsers.filter((u: ISelectedUser) => u.id !== user._id)
      : [...selectedUsers, { id: user._id, name: user.username }];
    setValue(name, next, { shouldValidate: true, shouldDirty: true });
  };

  const defaultLabel = name === 'excludedUsers' ? 'Utilisateurs exclus' : 'Sélection des utilisateurs';
  const placeholder = name === 'excludedUsers' ? 'Rechercher un utilisateur à exclure...' : 'Rechercher un utilisateur...';
  const chipSuffix = name === 'excludedUsers' ? 'exclu(s)' : 'sélectionné(s)';

  return (
    <div className='flex flex-col gap-1.5'>
      {showLabel && <label className='text-xs font-semibold text-muted-foreground'>{label ?? defaultLabel}</label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className={cn(
              'w-full justify-between bg-muted/30 focus:bg-background transition-colors h-9 md:h-10 font-normal px-3',
              selectedUsers.length === 0 && 'text-muted-foreground'
            )}
          >
            {selectedUsers.length > 0 ? (
              <span className='flex items-center gap-2 truncate'>
                <Users className='h-3.5 w-3.5 opacity-70' />
                <span className='font-medium'>{selectedUsers.length}</span>
                <span className='text-xs text-muted-foreground'>utilisateur(s) {chipSuffix}</span>
              </span>
            ) : (
              placeholder
            )}
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-(--radix-popover-trigger-width) p-0' align='start'>
          <div className='flex flex-col w-full bg-popover text-popover-foreground'>
            {/* Search Input */}
            <div className='flex items-center border-b px-3'>
              <Search className='mr-2 h-4 w-4 shrink-0 opacity-50' />
              <input
                className='flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50'
                placeholder='Rechercher par nom ou email...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {isLoading && <Loader2 className='ml-2 h-4 w-4 animate-spin opacity-50' />}
            </div>

            {/* Results List */}
            <div className='max-h-[200px] overflow-y-auto overflow-x-hidden p-1'>
              {!isLoading && users.length === 0 && (
                <div className='py-6 text-center text-sm text-muted-foreground'>Aucun utilisateur trouvé.</div>
              )}

              {users.map((user: ISearchUserData) => (
                <button
                  type='button'
                  key={user._id}
                  onClick={() => toggleUser(user)}
                  className={cn(
                    'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors text-left',
                    isSelected(user._id) && 'bg-accent text-accent-foreground'
                  )}
                >
                  <Check className={cn('mr-2 h-4 w-4', isSelected(user._id) ? 'opacity-100' : 'opacity-0')} />
                  <div className='flex flex-col flex-1 min-w-0'>
                    <span className='font-medium truncate'>{user.username}</span>
                    <span className='text-xs text-muted-foreground truncate'>{user.email}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <div className='mt-2 flex flex-wrap gap-2'>
        {selectedUsers.length === 0 && (
          <span className='text-xs text-muted-foreground'>Aucun utilisateur {chipSuffix}</span>
        )}
        {selectedUsers.map((user: ISelectedUser) => (
          <span
            key={user.id}
            className='inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs'
          >
            {user.name}
            <button
              className='ml-1 text-muted-foreground hover:text-foreground'
              onClick={() => toggleUser({ _id: user.id, username: user.name, email: '' })}
              type='button'
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export default UserMultiSelect;
